/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription, timer} from 'rxjs';
import {SpecialOrderDetailModel} from '../../api/ms-specialorders/contracts/special-order-detail.model';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {SpecialOrderService} from '../../api/ms-specialorders/specialorder/specialorder.service';
import {AvailableActions} from '../../api/ms-specialorders/contracts/available-actions.enum';
import {SpecialOrderStatus} from '../../api/ms-specialorders/contracts/special-order-status.enum';
import {StickyHeaderIconButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-icon-button.component';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {Permission} from '../../shared/utils/permission.enum';
import {UserService} from '../../shared/services/user.service';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {SpoActionsUtilService} from '../services/spo-actions-util/spo-actions-util.service';
import {SpecialOrderMaterialStatus} from '../../api/ms-specialorders/contracts/special-order-material-status.enum';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';

@Component({
    selector: 'app-spo-detail-page',
    templateUrl: './spo-detail-page.component.html',
    styleUrls: ['./spo-detail-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpoDetailPageComponent implements OnInit, OnDestroy {
    public spoDetails: SpecialOrderDetailModel;
    public contextMenuOptions: StickyHeaderIconButtonComponent[];
    public specialOrderStatus = SpecialOrderStatus;
    private subscriptions = new Subscription();

    constructor(
            private activatedRoute: ActivatedRoute,
            private router: Router,
            private modalService: NgbModal,
            private stickyHeaderService: StickyHeaderService,
            private specialOrderService: SpecialOrderService,
            private toast: ToastWrapperService,
            private userService: UserService,
            private loadingService: LoadingService,
            private spinnerService: SpinnerService,
            private spoActionsUtilService: SpoActionsUtilService) {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    ngOnInit(): void {
        const subscription = this.activatedRoute.data.subscribe(
                (data) => {
                    if (data.spoDetail) {
                        this.spoDetails = data.spoDetail;
                        this.showPartialSubmittedWarning();
                    } else {
                        this.router.navigate(['/', 'spo']);
                        const errorModalInstance: ConfirmModalComponent = this.modalService
                                .open(ConfirmModalComponent, {centered: true}).componentInstance;
                        errorModalInstance.initModal({
                            titleText: 'Error',
                            subtitleText: `The information that you are looking for is not available right now.`,
                            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
                            primaryButtonText: null,
                            secondaryButtonText: 'Close'
                        } as ConfirmModal);
                    }
                    this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                },
                (error) => {
                    this.router.navigate(['/', 'spo']);
                    this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                });

        this.subscriptions.add(subscription);

        setTimeout(() => {
            this.contextMenuOptions = [];

            if (this.userService.hasPermissions([Permission.PARTNER_SPO_EDIT])) {
                this.contextMenuOptions.push({
                    text: 'Edit',
                    icon: 'iwp-icon-gen_edit',
                    click: (): void => {
                        this.contextMenuEdit();
                    },
                    id: 'sticky-header-edit-button',
                    class: 'btn-text-link',
                    dataCy: 'sticky-header-edit-button',
                    disabled:
                            !this.spoDetails.availableActions || this.spoDetails.availableActions &&
                            !this.spoDetails.availableActions.includes(AvailableActions.EDIT) &&
                            !this.spoDetails.availableActions.includes(AvailableActions.EDIT_POSITION_DELETE) &&
                            !this.spoDetails.availableActions.includes(AvailableActions.EDIT_POSITION_CANCEL),
                    tooltip: 'Edit SPO'
                });
            }

            if (this.userService.hasPermissions([Permission.PARTNER_SPO_DELETE]) &&
                    this.spoDetails.availableActions && this.spoDetails.availableActions.includes(AvailableActions.DELETE)) {
                this.contextMenuOptions.push({
                    text: 'Delete',
                    icon: 'iwp-icon-gen_delete',
                    click: (): void => {
                        this.contextMenuDelete();
                    },
                    id: 'sticky-header-delete-button',
                    class: 'btn-text-link',
                    dataCy: 'sticky-header-delete-button',
                    tooltip: 'Delete SPO'
                });
            }

            if (this.userService.hasPermissions([Permission.PARTNER_SPO_CREATE])) {
                this.stickyHeaderService.registerMainAction({
                    text: 'New',
                    click: (): void => {
                        this.router.navigate(['/', 'spo', 'new']);
                    },
                    id: 'sticky-header-new-button',
                    class: 'btn-primary',
                    dataCy: 'sticky-header-new-button',
                    tooltip: 'Create new SPO'
                });
            }

            this.stickyHeaderService.registerContextActions(this.contextMenuOptions);

            if (this.userService.hasPermissions([Permission.PARTNER_SPO_SEND])
                    && (this.spoDetails.status.code === SpecialOrderStatus.DRAFT
                            || this.spoDetails.status.code === SpecialOrderStatus.OPEN)) {
                this.stickyHeaderService.registerSubmitAction({
                    text: `${this.spoActionsUtilService
                            .getContextMenuSendButtonText(this.spoDetails.availableActions.includes(AvailableActions.RESUBMIT))}`,
                    click: (): void => {
                        this.contextMenuSend();
                    },
                    id: 'sticky-header-send-button',
                    class: 'btn-primary dynamic-width',
                    dataCy: 'sticky-header-send-button',
                    disabled: this.spoDetails.availableActions
                            && (!this.spoDetails.availableActions.includes(AvailableActions.SUBMIT)
                                    && !this.spoDetails.availableActions.includes(AvailableActions.RESUBMIT)),
                    tooltip: 'Send SPO'
                });
            }
        });

        /** Subscription to handle page loading **/
        this.subscriptions.add(
                this.spoActionsUtilService.enableLoading.subscribe(isLoading => {
                    if (isLoading) {
                        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                    } else {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                    }
                })
        );
    }

    ngOnDestroy(): void {
        this.stickyHeaderService.clearActions();
        this.subscriptions.unsubscribe();
    }

    contextMenuEdit(): void {
        this.router.navigate(['/', 'spo', this.spoDetails.number, 'edit']);
    }

    contextMenuSend(): void {
        const draft = this.spoDetails != null && this.spoDetails.materialStatusGroups != null
                ? this.spoDetails.materialStatusGroups.find(s => s.status.code === SpecialOrderMaterialStatus.DRAFT)
                : null;
        const draftCount = draft != null ? draft.numberOfMaterials : 0;
        this.spoActionsUtilService.submitSpecialOrder(this.spoDetails.number, draftCount,
                this.spoDetails.availableActions.includes(AvailableActions.SUBMIT),
                this.spoDetails.availableActions.includes(AvailableActions.RESUBMIT),
                () => {
                    this.router.navigate(['/', 'spo']);
                });
    }

    contextMenuDelete(): void {
        this.spoActionsUtilService.deleteSpecialOrder(this.spoDetails.number, () => this.router.navigate(['/', 'spo']));
    }

    isPartialSubmittedSPO(): boolean {
        if (this.spoDetails == null || this.spoDetails.materialStatusGroups == null) {
            return false;
        }

        const draft = this.spoDetails.materialStatusGroups.find(s => s.status.code === SpecialOrderMaterialStatus.DRAFT);

        return draft != null
                && draft.numberOfMaterials > 0
                && this.spoDetails.availableActions.includes(AvailableActions.RESUBMIT);
    }

    showPartialSubmittedWarning(): void {
        if (this.isPartialSubmittedSPO()) {
            const draft = this.spoDetails.materialStatusGroups.find(s => s.status.code === SpecialOrderMaterialStatus.DRAFT);

            timer(0).subscribe(() => {
                this.toast.warning([`<p>This SPO contains ${draft.numberOfMaterials} draft material(s).</p>`,
                    `<p><strong>Please, resend this SPO in order to successfully receive the material(s).</strong></p>`,
                    `<p>Only the draft material(s) will be requested to the supplier.</p>`].join('<br>'), 'Warning');
            });
        }
    }
}
